import { Banner, Loader } from '@cloudflare/kumo';
import { createPortal } from 'react-dom';
import { CheckCircle, X } from '@phosphor-icons/react';
import { useToastContext, type ToastItem, type ToastType } from '../../contexts/ToastContext';

/**
 * Pilha de toasts do contexto, em `Banner`s do Kumo no canto superior direito.
 *
 * Quem controla o tempo de vida é o `ToastContext` (o `loading` nunca expira);
 * aqui só mapeamos o formato.
 */
const VARIANT: Record<ToastType, 'default' | 'alert' | 'error' | 'secondary'> = {
  success: 'default',
  info: 'secondary',
  warning: 'alert',
  error: 'error',
  'in-progress': 'secondary',
};

export function ToastFlashbar() {
  const { items, dismiss } = useToastContext();
  if (items.length === 0) return null;

  // Portal no body: dentro do shell o toast ficava atrás do conteúdo da página (stacking context).
  return createPortal(
    <div
      className="fixed right-4 top-16 z-[1000] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2"
      role="region"
      aria-label="Notificações"
      aria-live="polite"
    >
      {items.map((item: ToastItem) => (
        <div key={item.id} className="relative rounded-lg bg-kumo-base shadow-lg">
          <Banner
            variant={VARIANT[item.type]}
            icon={item.loading ? <Loader size={16} /> : item.type === 'success' ? <CheckCircle weight="fill" /> : undefined}
            title={item.title}
            description={item.description}
            className="pr-9"
          />
          <button
            type="button"
            aria-label="Fechar notificação"
            onClick={() => dismiss(item.id)}
            className="absolute right-2 top-2 rounded p-1 text-kumo-subtle hover:text-kumo-default"
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </div>,
    document.body,
  );
}
